import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, Scissors, Users, Zap, Activity, MoreHorizontal, X, Sparkles, Loader2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ICONS = {
  'trending-up': TrendingUp,
  'scissors': Scissors,
  'users': Users,
  'zap': Zap,
  'activity': Activity,
  'more-horizontal': MoreHorizontal
};

const DISMISS_KEY = 'cvna_onboarding_intent_dismissed_local';
const ANSWERED_KEY = 'cvna_onboarding_intent_answered';
const PICKED_KEY = 'cvna_onboarding_intent_picked'; // survives remounts during follow-up step

// Hardcoded options mirror the backend list. Used as a client-side fallback if the
// GET /api/onboarding/intent call transiently fails, so the banner can still render.
const FALLBACK_OPTIONS = [{ value: 'grow_revenue', label: 'Grow revenue', icon: 'trending-up' }, { value: 'cut_costs', label: 'Cut costs', icon: 'scissors' }, { value: 'understand_customers', label: 'Understand my customers', icon: 'users' }, { value: 'automate_operations', label: 'Automate operations', icon: 'zap' }, { value: 'track_performance', label: 'Track performance', icon: 'activity' }, { value: 'other', label: 'Something else', icon: 'more-horizontal' }];

/**
 * Post-signup inline dismissible survey.
 * Asks "What's your biggest priority?" once per user.
 * Routes user to the best-fit dashboard on selection.
 * Never blocks the UI — always skippable.
 */
export default function OnboardingIntentBanner() {
  const navigate = useNavigate();
  const { user} = useAuth();
  const [visible, setVisible] = useState(false);
  const [options, setOptions] = useState(FALLBACK_OPTIONS);
  const [submitting, setSubmitting] = useState(null); // intent value being saved
  const [error, setError] = useState(null);
  // Follow-up "what's stopping you" state
  const [pickedIntent, setPickedIntent] = useState(null);
  const [followupText, setFollowupText] = useState('');
  const [followupSubmitting, setFollowupSubmitting] = useState(false);

  // Fall back to localStorage user if AuthContext user hasn't resolved yet
  // (AuthContext fetches /api/auth/me which can fail intermittently in preview envs).
  let cachedUser = user;
  if (!cachedUser) {
    try {
      const raw = localStorage.getItem('corvana_user');
      if (raw) cachedUser = JSON.parse(raw);
    } catch (_) {}
  }
  const userKey = cachedUser?.id || cachedUser?.email || 'anon';
  const answeredKey = `${ANSWERED_KEY}:${userKey}`;

  useEffect(() => {
    // Persistent + session guards — if the user already answered or dismissed, don't show again
    if (sessionStorage.getItem(DISMISS_KEY) === '1') return;
    if (localStorage.getItem(answeredKey) === '1') return;
    if (!user) return; // wait for auth context
    // Don't render for unknown users (pre-auth resolution)
    if (userKey === 'anon') return;

    // Restore follow-up state if we remounted during the follow-up step
    try {
      const raw = sessionStorage.getItem(PICKED_KEY);
      if (raw) {
        const picked = JSON.parse(raw);
        if (picked && picked.intent) {
          setPickedIntent(picked);
          setVisible(true);
          return; // skip GET verify; follow-up already shown
        }
      }
    } catch (_) {}

    // Optimistic render: show the banner immediately with hardcoded options.
    // The server GET is only used to OPT-OUT if the user already answered.
    setOptions(FALLBACK_OPTIONS);
    setVisible(true);

    // Verify server-side state in the background (does not block rendering)
    (async () => {
      try {
        const res = await axios.get(`${window.location.origin}/api/onboarding/intent`, {
          
          validateStatus: () => true
        });
        if (res.status < 200 || res.status >= 300) return;
        const data = res.data;
        if (data.answered) {
          localStorage.setItem(answeredKey, '1');
          setVisible(false);
        } else if (Array.isArray(data.options) && data.options.length) {
          setOptions(data.options);
        }
      } catch (err) {
        // Non-critical — banner already rendered optimistically.
        console.warn('[OnboardingIntent] background verify failed', err);
      }
    })();
  }, [user, answeredKey, userKey]);

  const handleSelect = async (intent) => {
    setSubmitting(intent);
    setError(null);
    try {
      const res = await axios.post(
        `${window.location.origin}/api/onboarding/intent`,
        { intent },
        {
          
          validateStatus: () => true
        },
      );
      if (res.status < 200 || res.status >= 300) throw new Error('save_failed');
      const data = res.data;
      // Show the follow-up question instead of hiding immediately.
      // NOTE: do NOT write DISMISS_KEY/answeredKey here — that would cause the
      // component to early-return on any remount, losing the follow-up UI.
      // Persist intent+route to sessionStorage so a remount can restore the state.
      const picked = { intent, recommended_route: data.recommended_route };
      try { sessionStorage.setItem(PICKED_KEY, JSON.stringify(picked)); } catch (_) {}
      setPickedIntent(picked);
      setSubmitting(null);
    } catch (err) {
      setError('Could not save — please try again.');
      setSubmitting(null);
    }
  };

  const submitFollowup = async (skip = false) => {
    if (!pickedIntent) return;
    setFollowupSubmitting(true);
    const noteToSend = skip ? null : (followupText || '').trim().slice(0, 512);
    try {
      if (noteToSend) {
        await axios.post(
          `${window.location.origin}/api/onboarding/intent`,
          { intent: pickedIntent.intent, note: noteToSend },
          {
            
            validateStatus: () => true
          },
        );
      }
    } catch (_) {
      // Best-effort — don't block navigation on failure
    }
    // NOW mark as complete — after follow-up step is done
    sessionStorage.setItem(DISMISS_KEY, '1');
    localStorage.setItem(answeredKey, '1');
    // Save the user's preferred dashboard so future /app visits auto-redirect there
    if (pickedIntent.recommended_route) {
      localStorage.setItem(`cvna_default_dashboard_route:${userKey}`, pickedIntent.recommended_route);
    }
    try { sessionStorage.removeItem(PICKED_KEY); } catch (_) {}
    setVisible(false);
    if (pickedIntent.recommended_route) {
      setTimeout(() => navigate(pickedIntent.recommended_route), 150);
    }
  };

  const handleDismiss = async () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    localStorage.setItem(answeredKey, '1');
    setVisible(false);
    try {
      await axios.post(
        `${window.location.origin}/api/onboarding/intent/dismiss`,
        {},
        {
          
          validateStatus: () => true
        },
      );
    } catch (_) {
      // Silent — dismiss is best-effort
    }
  };

  if (!visible) return null;

  // Once user picked an intent, swap the banner content to the follow-up question.
  if (pickedIntent) {
    const pickedLabel = options.find((o) => o.value === pickedIntent.intent)?.label || pickedIntent.intent;
    return (
      <div
        data-testid="onboarding-intent-followup"
        className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-4"
      >
        <div className="relative rounded-2xl border border-border/60 bg-gradient-to-br from-primary/5 via-background to-background shadow-sm p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-3">
            <div className="mt-0.5 rounded-lg bg-primary/10 p-2">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-foreground">
                Got it — "{pickedLabel}". One more thing.
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                What's the biggest thing getting in the way of that right now? (optional, stays between us)
              </p>
            </div>
          </div>
          <textarea
            data-testid="onboarding-intent-followup-input"
            value={followupText}
            onChange={(e) => setFollowupText(e.target.value)}
            placeholder="e.g. I can't tell which marketing is actually working, I keep losing customers after 30 days, I don't have time to look at 5 dashboards…"
            maxLength={512}
            rows={3}
            disabled={followupSubmitting}
            className="w-full rounded-xl border border-border/70 bg-card px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none disabled:opacity-50"
          />
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => submitFollowup(true)}
              disabled={followupSubmitting}
              data-testid="onboarding-intent-followup-skip"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              Skip & continue
            </button>
            <button
              type="button"
              onClick={() => submitFollowup(false)}
              disabled={followupSubmitting}
              data-testid="onboarding-intent-followup-submit"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {followupSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Send & continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="onboarding-intent-banner"
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-4"
    >
      <div className="relative rounded-2xl border border-border/60 bg-gradient-to-br from-primary/5 via-background to-background shadow-sm p-5 sm:p-6">
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="onboarding-intent-dismiss"
          className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <div className="mt-0.5 rounded-lg bg-primary/10 p-2">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-foreground">
              Welcome — what's your biggest priority right now?
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Pick one and we'll take you straight to the dashboard built for it. You can always change later.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
          {options.map((opt) => {
            const Icon = ICONS[opt.icon] || MoreHorizontal;
            const isSubmitting = submitting === opt.value;
            const isDisabled = submitting !== null;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={isDisabled}
                onClick={() => handleSelect(opt.value)}
                data-testid={`onboarding-intent-option-${opt.value}`}
                className="group flex items-center gap-2.5 text-left rounded-xl border border-border/70 bg-card px-3 py-2.5 sm:px-4 sm:py-3 hover:border-primary/50 hover:bg-primary/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="flex-shrink-0 rounded-md bg-muted p-1.5 group-hover:bg-primary/10 transition-colors">
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  ) : (
                    <Icon className="h-4 w-4 text-foreground/80 group-hover:text-primary" />
                  )}
                </span>
                <span className="text-sm font-medium text-foreground">{opt.label}</span>
              </button>
            );
          })}
        </div>

        {error && (
          <p
            data-testid="onboarding-intent-error"
            className="mt-3 text-xs text-destructive"
          >
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={handleDismiss}
            data-testid="onboarding-intent-skip"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
